import React, { useState, useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "react-hot-toast";
import { useUserContext } from "../../context/UserContext";
import FilterConfigForm from "./FilterConfigForm";
import ReplaceParts from "./ReplaceParts";

export default function ProductDetails() {
  const { productId } = useParams();
  const location = useLocation();
  const { token } = useUserContext();

  const [formData, setFormData] = useState({
    product_id: productId || "",
    device_model: "",
    activation_date: "",
    ro_id: "",
    alk_id: "",
    carbon_id: "",
    sed_id: "",
    ro_at: "",
    alk_at: "",
    carbon_at: "",
    sed_at: "",
    max_flow_rate: "",
    pressure_threshold: "",
    service_interval: "",
    device_id: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState(location.state?.tab === "replace" ? "replace" : "ro");
  const [installationDate, setInstallationDate] = useState("");
  const [updating, setUpdating] = useState(false);

  const handleUpdateInstallationDate = async (e) => {
    e.preventDefault();
    if (!formData.product_id || !installationDate) return;
    setUpdating(true);
    try {
      await axios.put(`${process.env.REACT_APP_API_URL}/components/${formData.product_id}`, {
        installation_date: installationDate,
      }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      toast.success('Installation date updated successfully');
    } catch (err) {
      console.error('Failed to update installation date', err);
      toast.error('Failed to update installation date');
    } finally {
      setUpdating(false);
    }
  };

  useEffect(() => {
    let mounted = true;
    async function fetchDetails() {
      if (!productId) return;
      // if product object was passed via navigation state, use it
      const productFromState = location?.state?.product;
      const stateProductId = productFromState?.product_id || productFromState?.id;
      if (productFromState && String(stateProductId) === String(productId)) {
        setFormData((prev) => ({
          ...prev,
          product_id: productFromState.product_id || productFromState.id,
          device_model: productFromState.product_name || productFromState.device_model || prev.device_model,
          activation_date: productFromState.activation_date || prev.activation_date,
          ro_id: productFromState.ro_mem_id || productFromState.ro_id || (productFromState.ro_mem && productFromState.ro_mem.component_id) || prev.ro_id,
          alk_id: productFromState.alkaline_id || productFromState.alk_id || (productFromState.alkaline && productFromState.alkaline.component_id) || prev.alk_id,
          carbon_id: productFromState.carbon_id || (productFromState.carbon && productFromState.carbon.component_id) || prev.carbon_id,
          sed_id: productFromState.sed_id || productFromState.sediment_id || (productFromState.sed && productFromState.sed.component_id) || prev.sed_id,
          ro_at: productFromState.ro_at || (productFromState.ro_mem && productFromState.ro_mem.installed_at) || prev.ro_at,
          alk_at: productFromState.alk_at || (productFromState.alkaline && productFromState.alkaline.installed_at) || prev.alk_at,
          carbon_at: productFromState.carbon_at || (productFromState.carbon && productFromState.carbon.installed_at) || prev.carbon_at,
          sed_at: productFromState.sed_at || (productFromState.sed && productFromState.sed.installed_at) || prev.sed_at,
          max_flow_rate: productFromState.max_flow_rate || prev.max_flow_rate,
          pressure_threshold: productFromState.pressure_threshold || prev.pressure_threshold,
          service_interval: productFromState.service_interval || productFromState.service_interval_months || prev.service_interval,
          device_id: productFromState.device_id || productFromState.deviceId || prev.device_id,
        }));
        return;
      }
      setLoading(true);
      setError("");
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/inventory/products/${productId}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const p = res.data?.data?.data ?? res.data?.data ?? res.data;
        if (mounted && p) {
          setFormData((prev) => ({
            ...prev,
            product_id: p.product_id || p.id || prev.product_id,
            device_model: p.device_model || p.model || p.deviceModel || prev.device_model,
            activation_date: p.activation_date || p.effective_from || prev.activation_date,
            ro_id: p.ro_id || p.ro || p.roMemId || prev.ro_id,
            alk_id: p.alk_id || p.alk || p.alkalineId || prev.alk_id,
            carbon_id: p.carbon_id || p.carbon || p.carbonFilterId || prev.carbon_id,
            sed_id: p.sediment_id || p.sed_id || p.sediment || prev.sed_id,
            ro_at: p.ro_mem?.installed_at || p.ro_at || prev.ro_at,
            alk_at: p.alkaline?.installed_at || p.alk_at || prev.alk_at,
            carbon_at: p.carbon?.installed_at || p.carbon_at || prev.carbon_at,
            sed_at: p.sed?.installed_at || p.sed_at || prev.sed_at,
            max_flow_rate: p.max_flow_rate || p.flow_max || prev.max_flow_rate,
            pressure_threshold: p.pressure_threshold || prev.pressure_threshold,
            service_interval: p.service_interval_months || p.service_interval || prev.service_interval,
            device_id: p.device_id || p.deviceId || prev.device_id,
          }));
        }
      } catch (err) {
        console.warn(err);
        if (mounted) setError("Failed to load product details");
      } finally {
        if (mounted) setLoading(false);
      }
    }
    fetchDetails();
    return () => { mounted = false; };
  }, [productId, token, location?.state?.product]);

  useEffect(() => {
    if (activeTab === 'change_installation' && formData.ro_id) {
      async function fetchInstallationDate() {
        try {
          const res = await axios.get(`${process.env.REACT_APP_API_URL}/components/${formData.ro_id}`, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          });
          const comp = res.data?.data ?? res.data;
          if (comp && comp.installed_at) {
            const date = new Date(comp.installed_at).toISOString().split('T')[0];
            setInstallationDate(date);
          }
        } catch (err) {
          console.warn('Failed to fetch installation date', err);
        }
      }
      fetchInstallationDate();
    }
  }, [activeTab, formData.ro_id, token]);

  const filterTabs = [
    { key: 'replace', label: 'Replace' },
    { key: 'ro', label: 'RO Config' },
    { key: 'alk', label: 'Alkaline Config' },
    { key: 'sediment', label: 'Sediment Config' },
    { key: 'carbon', label: 'Carbon Config' },
    { key: 'change_installation', label: 'Change Installation Date' },
  ];

  return (
    <div className="w-full min-h-full px-3 py-6 sm:px-6 md:px-8 max-w-5xl mx-auto flex justify-center">
      <div className="w-full bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
        {/* Tabs Header */}
        <div className="flex overflow-x-auto whitespace-nowrap bg-indigo-600 border-b no-scrollbar">
          {filterTabs.map((tab) => (
            <button
              key={tab.key}
              className={`px-4 sm:px-6 py-3 font-medium text-xs sm:text-sm focus:outline-none transition-colors shrink-0 ${
                activeTab === tab.key
                  ? 'bg-white text-indigo-700 font-bold shadow-sm'
                  : 'text-indigo-100 hover:bg-indigo-700 hover:text-white'
              }`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="p-4 sm:p-6 border-b border-gray-200 bg-gray-50 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <h2 className="text-lg sm:text-xl font-bold text-gray-800">Product: {formData.product_id}</h2>
          <div className="flex flex-wrap gap-2 sm:gap-4 text-xs text-gray-500">
            {formData.ro_at && <span className="bg-white px-2 py-1 rounded border">RO: {new Date(formData.ro_at).toLocaleDateString()}</span>}
            {formData.alk_at && <span className="bg-white px-2 py-1 rounded border">Alk: {new Date(formData.alk_at).toLocaleDateString()}</span>}
            {formData.carbon_at && <span className="bg-white px-2 py-1 rounded border">Carb: {new Date(formData.carbon_at).toLocaleDateString()}</span>}
            {formData.sed_at && <span className="bg-white px-2 py-1 rounded border">Sed: {new Date(formData.sed_at).toLocaleDateString()}</span>}
          </div>
        </div>

        {activeTab === 'replace' ? (
          <ReplaceParts productId={formData.product_id || productId} deviceId={formData.device_id} />
        ) : loading ? (
          <div className="p-4 sm:p-8 text-gray-500">Loading product...</div>
        ) : error ? (
          <div className="p-4 sm:p-8 text-red-500">{error}</div>
        ) : activeTab === 'change_installation' ? (
          <div className="p-4 sm:p-8">
            <h3 className="text-lg font-semibold mb-4 text-gray-800">Change Installation Date for RO Component</h3>
            <form onSubmit={handleUpdateInstallationDate} className="space-y-4 max-w-md">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Installation Date</label>
                <input
                  type="date"
                  value={installationDate}
                  onChange={(e) => setInstallationDate(e.target.value)}
                  className="mt-1 block w-full border border-gray-300 rounded-lg p-2.5 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                  required
                />
              </div>
              <button
                type="submit"
                disabled={updating}
                className="w-full sm:w-auto px-5 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 text-sm font-semibold transition-colors"
              >
                {updating ? 'Updating...' : 'Update Date'}
              </button>
            </form>
          </div>
        ) : (
          <div className="p-4 sm:p-6 md:p-8">
            <FilterConfigForm
              type={activeTab}
              productId={formData.product_id}
              isEmbedded={true}
            />
          </div>
        )}
      </div>
    </div>
  );
}
